import { Export } from "@phosphor-icons/react";
import clsx from "clsx";
import { type JSX, useState } from "react";
import { useTranslation } from "react-i18next";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import Checkbox from "common/components/ui/Checkbox";
import { useCampaignContext } from "common/helpers/CampaignContext";

import useAudience from "../../../datahooks/useAudience";
import { exportProfilesToCSV } from "../../../utils";
import SectionHeading from "../../SectionHeading";
import AddTarget from "./AddTarget";
import AudienceList from "./AudienceList";
import FiltersPopover from "./FiltersPopover";
import RemoveTargets from "./RemoveTargets";

import type { AudienceProfileState } from "../../../types";

import EmptyPlaceholderImage from "assets/images/empty-placeholders/no-profiles.png";

export default function CampaignAudience({
  openLeadPreview,
}: {
  openLeadPreview: (leadId: string) => void;
}) {
  const { t } = useTranslation("campaigns");
  const [inputValue, setInputValue] = useState("");
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>([]);
  const [selectedStates, setSelectedStates] = useState<AudienceProfileState[]>(
    [],
  );

  const { custom_variable_keys: customVariables } = useCampaignContext();

  const { audience, isLoadingAudience, isAudienceError, refetchAudience } =
    useAudience();

  if (isAudienceError)
    return (
      <ErrorPlaceholder
        errorMessage="Error loading audience"
        onRetry={refetchAudience}
      />
    );

  function resetFilters() {
    setSelectedStates([]);
    setInputValue("");
  }

  function resetSelectedProfiles() {
    setSelectedProfiles([]);
  }

  const lowercasedInput = inputValue.toLowerCase();

  const filteredTargets = audience
    ? audience.filter(({ full_name, state }) => {
        const isMatchingInput = lowercasedInput
          ? full_name.toLowerCase().includes(lowercasedInput)
          : true;

        const isMatchingState = selectedStates.length
          ? selectedStates.includes(state)
          : true;

        return isMatchingInput && isMatchingState;
      })
    : [];

  const profilesToExport = selectedProfiles.length
    ? audience.filter(({ urn }) => selectedProfiles.includes(urn))
    : filteredTargets;

  let mainComponent: JSX.Element;

  if (isLoadingAudience || filteredTargets.length) {
    mainComponent = (
      <AudienceList
        audience={isLoadingAudience ? new Array(12).fill({}) : filteredTargets}
        isLoading={isLoadingAudience}
        selectedProfiles={selectedProfiles}
        setSelectedProfiles={setSelectedProfiles}
        openLeadPreview={openLeadPreview}
      />
    );
  } else if (!audience.length) {
    mainComponent = (
      <EmptyPlaceholder
        imageSrc={EmptyPlaceholderImage}
        title="No targets found"
        subtitle="This campaign does not have any targets. You can add targets through integrations, via API, or manually."
      />
    );
  } else {
    mainComponent = (
      <EmptyPlaceholder
        imageSrc={EmptyPlaceholderImage}
        title="No targets found"
        subtitle={
          inputValue
            ? `No targets found for “${inputValue}”`
            : "No results found for the selected filters"
        }
        actionComponent={
          <Button onClick={resetFilters}>
            Reset {inputValue ? "search" : "filters"}
          </Button>
        }
      />
    );
  }

  return (
    <>
      <SectionHeading
        title={t("campaignDetails.audienceTitle")}
        subtitle={t("campaignDetails.audienceSubtitle")}
      />

      <section className="flex flex-col gap-4 lg:flex-row-reverse">
        <div className="flex items-center gap-x-2">
          <RenderIf condition={!!selectedProfiles.length}>
            <Button
              variant="outline-black"
              intent="iconOnly"
              onClick={resetSelectedProfiles}
            >
              <div>
                <Checkbox isStyleOnly checked="indeterminate" />
              </div>
            </Button>
          </RenderIf>

          <RestrictedComponent visibleForPermissions={["campaigns_full"]}>
            {selectedProfiles.length ? (
              <RemoveTargets
                selectedProfiles={selectedProfiles}
                onSuccess={resetSelectedProfiles}
              />
            ) : (
              <RenderIf condition={!customVariables.length}>
                <AddTarget />
              </RenderIf>
            )}
          </RestrictedComponent>

          <Button
            leftIcon={<Export weight="fill" />}
            variant="secondary-purple"
            className={clsx(
              "max-lg:flex-1",
              !filteredTargets.length &&
                "pointer-events-none bg-black-200 text-black-400",
            )}
            asChild
          >
            <a
              href={exportProfilesToCSV(profilesToExport)}
              download="LinkedInProfiles.csv"
            >
              <span className="truncate">
                Export{" "}
                {selectedProfiles.length
                  ? `${selectedProfiles.length} Target${selectedProfiles.length > 1 ? "s" : ""}`
                  : `audience (${filteredTargets.length})`}
              </span>
            </a>
          </Button>
        </div>
        <div className="flex flex-1 items-center gap-x-2 lg:mr-auto">
          <SearchInput
            value={inputValue}
            placeholder="Search targets"
            onChange={(e) => setInputValue(e.currentTarget.value)}
            className="flex-1 lg:max-w-80"
            onClear={() => setInputValue("")}
          />
          <FiltersPopover
            selectedStates={selectedStates}
            setSelectedStates={setSelectedStates}
            audience={audience}
          />
        </div>
      </section>

      {mainComponent}
    </>
  );
}
